import {
  Card,
  CardContent,
  CardOverflow,
  CircularProgress,
  Grid,
  IconButton,
} from '@mui/joy'
import Typography from '@mui/joy/Typography'
import AspectRatio from '@mui/joy/AspectRatio'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import RedeemIcon from '@mui/icons-material/Redeem'
import {
  useAddToMyStoresMutation,
  useGetAllStoresQuery,
  useGetMyStoresQuery,
  useRemoveFromMyStoresMutation,
} from '../services/stores'
import { useGetMyRewardsQuery } from '../services/rewards'
import StoreLoading from './StoreLoading'
import Store from '../types/store'

type StoreProps = {
  store: Store
  isAddEnabled?: boolean
  clickable?: boolean
}

function StoreLayout(props: StoreProps) {
  const navigate = useNavigate()

  const [addToMyStores, { isLoading: isAddToMyStoresLoading }] =
    useAddToMyStoresMutation()
  const { data: myStores, isLoading: isGetMyStoresLoading } =
    useGetMyStoresQuery()
  const { isLoading: isGetAllStoresLoading } = useGetAllStoresQuery()

  const { data: rewards, isLoading: isGetRewardsLoading } =
    useGetMyRewardsQuery(props.store.id)

  const [removeFromMyStores, { isLoading: isRemoveFromMyStoresLoading }] =
    useRemoveFromMyStoresMutation()

  const rewardsLength = rewards?.length ?? 0

  function handleAddStore() {
    addToMyStores({ storeId: props.store.id })
  }

  function goTo(route: string) {
    if (props.clickable) {
      navigate(route)
    }
  }

  function addOrDeleteStore() {
    if (myStores?.some((store) => store.id === props.store.id)) {
      removeFromMyStores(props.store.id)
    } else {
      handleAddStore()
    }
  }

  function getPointer() {
    return props.clickable ? 'pointer' : 'default'
  }

  if (isGetAllStoresLoading || isGetMyStoresLoading || isGetRewardsLoading) {
    return <StoreLoading />
  }

  return (
    <Grid sx={{ pt: 1, cursor: getPointer(), position: 'relative' }}>
      <Card size="sm" onClick={() => goTo(`/${props.store.id}`)} sx={{ pt: 0 }}>
        <Grid>
          {props.isAddEnabled && (
            <IconButton
              variant="plain"
              size="sm"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                position: 'absolute',
                top: '.5rem',
                right: '.5rem',
                zIndex: 2,
              }}
              onClick={(event) => {
                event.stopPropagation()
                addOrDeleteStore()
              }}
            >
              {isAddToMyStoresLoading || isRemoveFromMyStoresLoading ? (
                <CircularProgress />
              ) : myStores?.some((store) => store.id === props.store.id) ? (
                <FavoriteIcon sx={{ color: '#C41C1D' }} />
              ) : (
                <FavoriteBorderIcon />
              )}
            </IconButton>
          )}
          <CardOverflow>
            <AspectRatio ratio="1.75">
              <img
                style={{
                  borderRadius: '5px',
                }}
                src={props.store.logoUrl}
                alt="store"
              />
            </AspectRatio>
          </CardOverflow>

          <CardContent orientation="horizontal">
            <Grid container direction="column" sx={{ pt: 1, pl: 1 }}>
              <Typography level="title-lg">{props.store.name}</Typography>
              <Typography level="body-md">{props.store.type}</Typography>
              <Typography level="body-sm">{props.store.location}</Typography>
            </Grid>
          </CardContent>
        </Grid>
        {!props.isAddEnabled && rewardsLength > 0 && (
          <IconButton
            color="success"
            variant="plain"
            size="sm"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              position: 'absolute',
              top: '.5rem',
              right: '.5rem',
              zIndex: 2,
            }}
            onClick={(event) => {
              event.stopPropagation()
              goTo(`/${props.store.id}/rewards`)
            }}
          >
            <RedeemIcon sx={{ cursor: 'pointer' }} />
          </IconButton>
        )}
      </Card>
    </Grid>
  )
}

export default StoreLayout
